import {
  ConnectionStatusIndicator,
  ConnectionType,
  ICON_SIZES,
  type MarketDataSnapshot,
  Tbody,
  Td,
  type WebsocketError,
  connectionStatusText,
  isConnectionRecovering,
  useConnectionStatusContext,
  useMarketAccountsContext,
  useMarketsContext,
  useObservableValue,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { type Observable, distinctUntilChanged, map } from 'rxjs';
import { MarketDisplayName, Wrapper } from './styles';

export function MarketDataStatusTable({
  snapshots,
  websocketError,
  ...props
}: {
  snapshots: Observable<MarketDataSnapshot>;
  websocketError?: WebsocketError;
}) {
  const { marketsByName, isMarketConfigured } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketCredentialsWithConnectionStatusByMarketAccountName } = useConnectionStatusContext();

  const statuses = useObservableValue(
    () =>
      snapshots.pipe(
        map(snapshot => {
          const ms = snapshot.Markets ?? {};
          return Object.keys(ms)
            .filter(m => isMarketConfigured(m, ConnectionType.MarketData))
            .map(m => ({ Market: m, ...ms[m] }));
        }),
        distinctUntilChanged((p, n) => isEqual(p, n))
      ),
    [snapshots, isMarketConfigured],
    []
  );

  if (statuses == null) {
    return null;
  }

  return (
    <Wrapper data-testid="market-data-status-table" compact {...props}>
      <Tbody>
        {websocketError ? (
          <tr key="error">
            <Td textAlign="left">{websocketError.msg}</Td>
          </tr>
        ) : (
          statuses.map((s, i) => {
            const displayName = (marketAccountsByName.get(s.MarketAccount!) ?? marketsByName.get(s.Market))
              ?.DisplayName;
            const marketConnectionStatus = marketCredentialsWithConnectionStatusByMarketAccountName.get(
              s.MarketAccount!
            );
            const isRecovering = isConnectionRecovering(marketConnectionStatus?.connectionStatus);
            const status = s.Status;

            if (!displayName) {
              return null;
            }
            return (
              <tr key={i} data-testid={`market/${displayName}`}>
                <MarketDisplayName>{displayName}</MarketDisplayName>
                <Td textAlign="right">
                  <ConnectionStatusIndicator
                    status={status}
                    text={connectionStatusText(status)}
                    popoverText={isRecovering ? 'Recovering' : s.Text}
                    isLoading={isRecovering}
                    size={ICON_SIZES.SMALL}
                    iconRight
                  />
                </Td>
              </tr>
            );
          })
        )}
      </Tbody>
    </Wrapper>
  );
}
